import React, { useState, useEffect } from 'react';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';

const resolveImg = (src) => {
  if (!src) return src;
  if (src.startsWith('/frontend/')) return src.replace('/frontend/', '/');
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const Reveal = ({ children, delayMs = 0, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);
  return (
    <div className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>
  );
};

const AboutGroup = () => {
  const [sectionData, setSectionData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await API.get('/home-sections');
        if (res.data.data) setSectionData(res.data.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const hero = sectionData.group_hero || {};
  const intro = sectionData.group_intro || {};
  const vm = sectionData.group_vision_mission || {};
  const philosophy = sectionData.group_philosophy || {};

  const heroImage = hero.image ? resolveImg(hero.image) : `${process.env.PUBLIC_URL}/images/page-banners/about-us.jpg`;
  const introImage = intro.image ? resolveImg(intro.image) : `${process.env.PUBLIC_URL}/images/about-us.jpg`;
  const philosophyImage = philosophy.image ? resolveImg(philosophy.image) : `${process.env.PUBLIC_URL}/images/our-philosophy-Cambridge.jpeg`;

  return (
    <>
      <SEO
        title="About Cambridge Group"
        description="Learn more about the Cambridge Group of Schools and our legacy of educational excellence."
      />

      {/* HERO BANNER */}
      <PageBanner
        title={hero.heading || 'About Cambridge Group'}
        subtitle={hero.sub_heading || ''}
        backgroundImage={heroImage}
        loading={loading}
      />

      {/* INTRO SECTION */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <Reveal className="order-1 lg:order-2">
              {!loading && (
                  <img src={introImage} alt="Cambridge Group" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg" loading="lazy" />
              )}
            </Reveal>
            <div className="order-2 lg:order-1">
              <Reveal>
                <div className="kicker">{intro.kicker || 'Our Legacy'}</div>
                <h2 className="h-section text-3xl md:text-4xl font-bold mt-3">{intro.heading || 'Cambridge Group of Schools'}</h2>
                <div className="divider-line mt-5 !ml-[10px] !mr-auto" />
              </Reveal>
              <Reveal delayMs={70}>
                <div className="mt-8 space-y-4 text-body text-[17px] leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: intro.paragraphs || '<p>Cambridge Group of Schools stands as a beacon of educational excellence with a rich legacy of nurturing young minds. Over the years, we have grown into a network of premier institutions dedicated to academic brilliance, holistic development, and shaping the leaders of tomorrow.</p>' }} />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* VISION & MISSION BLOCKS */}
      <section className="section section-alt">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-[1200px] mx-auto">
            <Reveal>
              <div className="card p-7 md:p-9 h-full relative overflow-hidden bg-white shadow-sm border border-gray-100 rounded-2xl">
                <div className="absolute -right-12 -top-12 w-28 h-28 rounded-full bg-secondary/15" aria-hidden />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <i className="fas fa-eye" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-primary text-xl font-bold mb-3">{vm.vision_heading || 'Our Vision'}</h3>
                    <div className="text-body text-[17px] leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: vm.vision_text || '<p>To create a global community of lifelong learners, deeply rooted in ethical values, equipped with modern skills, and empowered to lead with compassion and innovation.</p>' }} />
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delayMs={90}>
              <div className="card p-7 md:p-9 h-full relative overflow-hidden bg-white shadow-sm border border-gray-100 rounded-2xl">
                <div className="absolute -right-12 -top-12 w-28 h-28 rounded-full bg-secondary/15" aria-hidden />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <i className="fas fa-bullseye" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-primary text-xl font-bold mb-3">{vm.mission_heading || 'Our Mission'}</h3>
                    <div className="text-body text-[17px] leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: vm.mission_text || '<p>Our mission across all Cambridge campuses is to provide a nurturing, inclusive, and forward-thinking educational ecosystem. We strive to foster academic excellence, promote critical thinking, and encourage holistic growth, ensuring that every student is prepared to confidently navigate the challenges of the future.</p>' }} />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY SECTION */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Reveal>
                <div className="kicker">{philosophy.kicker || 'Guiding Principles'}</div>
                <h2 className="h-section text-3xl md:text-4xl font-bold mt-3">{philosophy.heading || 'Our Philosophy'}</h2>
                <div className="divider-line mt-5 !ml-[10px] !mr-auto" />
              </Reveal>
              <Reveal delayMs={70}>
                <div className="mt-8 space-y-4 text-body text-[17px] leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: philosophy.paragraphs || '<p>At Cambridge Group, we believe that education is not just about academic excellence, but about fostering a love for learning, encouraging critical thinking, and building strong character. We aim to nurture every childs potential, providing them with the support, resources, and opportunities they need to become responsible, compassionate, and successful global citizens.</p>' }} />
              </Reveal>
            </div>
            <Reveal className="order-1 lg:order-2">
              {!loading && (
                  <img src={philosophyImage} alt="Our Philosophy" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg" loading="lazy" />
              )}
            </Reveal>
          </div>
        </div>
      </section>

    </>
  );
};

export default AboutGroup;
